import { Component, ViewChild, EventEmitter, Output, HostListener, OnInit } from '@angular/core';
import { Feature } from '@main/feature';
import { faEllipsis, faPencil, faPlus, faQuestionCircle, faSave, faTrashAlt, faGripVertical, faChevronUp, faChevronDown, faArchive } from '@fortawesome/free-solid-svg-icons';
import { FeatureService } from '@main/feature.service';
import { FeatureItemService } from '@main/feature-item.service';
import { AlertService } from '../../../alert.service';
import { NgbModal, NgbModalRef, NgbPopover, NgbAccordionDirective, NgbAccordionItem, NgbAccordionHeader, NgbAccordionToggle, NgbAccordionButton, NgbTypeahead, NgbCollapse, NgbAccordionCollapse, NgbAccordionBody, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, debounceTime, distinctUntilChanged, filter, lastValueFrom, merge, switchMap } from 'rxjs';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators, ReactiveFormsModule } from '@angular/forms';
import { FeatureItem } from '../../../feature-item';
import { InventoryService } from '@main/inventory.service';
import { Item } from '@main/item';
import { ConfirmationModalComponent } from '@main/confirmation-modal/confirmation-modal.component';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { ActivatedRoute } from '@angular/router';
import { SettingService } from '../../../setting.service';
import { FeatureGroupTemplateService } from '../../services/feature-group-template.service';
import { LoaderService } from '@app-shared/services/loader.service';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgFor, NgIf, CurrencyPipe, NgClass } from '@angular/common';

@Component({
    selector: 'app-feature-group-template',
    templateUrl: './feature-group-template.component.html',
    styleUrls: ['./feature-group-template.component.css'],
    imports: [ReactiveFormsModule, NgbAccordionDirective, NgFor, CdkDropList, CdkDrag, CdkDragHandle, NgbAccordionItem, NgbAccordionHeader, NgbAccordionToggle, NgbAccordionButton, FaIconComponent, NgIf, NgbTypeahead, NgbCollapse, NgbAccordionCollapse, NgbAccordionBody, NgbPopover, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgxSpinnerComponent, CurrencyPipe, NgClass]
})
export class FeatureGroupTemplateComponent implements OnInit {
  //Screensize event Listener
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.resizeSubject.next((event.target as Window).innerWidth);
  }

  // Variable Subject
  private resizeSubject: Subject<number> = new Subject<number>();

  @Output() saved: EventEmitter<void> = new EventEmitter<void>();

  // Modals
  modalReference!: NgbModalRef;
  @ViewChild('featureModal') private featureModal: NgbModalRef;
  @ViewChild('featureItemModal') private featureItemModal: NgbModalRef;
  @ViewChild('priceUpdateModal') private priceUpdateModal: NgbModalRef;

  // Features
  features: any[] = [];
  rawFeatures: any[] = [];
  featureForms!: UntypedFormGroup;
  proposalTemplateForm!: UntypedFormGroup;
  newFeatureForm!: UntypedFormGroup;
  selectedFeatureId!: string;
  agreementType: any = 'SINGLE';
  isMobileView: boolean = false;

  // Feature Name Typeaheads
  featureNameTypeaheads: Map<string, { input: (text$: Observable<string>) => Observable<string[]>, focus$: Subject<string> }> = new Map<string, { input: (text$: Observable<string>) => Observable<string[]>, focus$: Subject<string> }>();

  // New Feature Name Typeahead
  newFeatureNameTypeaheadInput!: (text$: Observable<string>) => Observable<string[]>;
  newFeatureNameTypeaheadFocus$!: Subject<string>;

  // Feature Items
  featureItems: Map<string, FeatureItem[]> = new Map<string, FeatureItem[]>();
  featureItemForm!: UntypedFormGroup;
  selectedFeatureItem!: any;
  featureItemPropertyUpdated$: Subject<any> = new Subject<any>();
  featureUpdated$: Subject<any> = new Subject<any>();

  // Inventory Items
  itemCategories: string[] = [];
  itemCategoryItems = new Map();
  selectedItemCategoryItems: Item[] = [];
  selectedItemStockLevels: { stockCurrentLevel: number, stockAdjustedLevel: number };
  showArchivedItems: boolean = true;
  currentCategoryItems: any[] = [];

  // Multi Options
  multiOptionKeys: any[] = [];
  multiOptionsRef: any;
  selectedMultiOptionKey: string;
  selectedMultiOptionPricing: any;

  // Font Awesome Properties
  faPlus = faPlus;
  faSave = faSave;
  faPencil = faPencil;
  faTrashAlt = faTrashAlt;
  faQuestionCircle = faQuestionCircle;
  faEllipsis = faEllipsis;
  faGripVertical = faGripVertical;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faArchive = faArchive;

  // Actions
  isCollapsed = false;
  isAccepted: boolean;
  isSaving: boolean;

  proposalId: any = '';
  proposalTemplate: any = null;
  pagreementType: any = 'SINGLE';

  // Price Update - Track selected feature items
  selectedFeatureItemsForPriceUpdate: Set<string> = new Set<string>();
  itemsToUpdate: any[] = [];

  // Get isMobile value in reactive way
  public get isMobile(): boolean {
    return this.isMobileView;
  }

  constructor(
    private inventoryService: InventoryService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private settingService: SettingService,
    private featureGroupTemplateService: FeatureGroupTemplateService,
    private alertService: AlertService,
    private loaderService: LoaderService
  ) {
    this.resizeSubject.pipe(
      debounceTime(100)
    ).subscribe(width => {
      if (width <= 768) {
        this.checkScreenSize(width);
      } else {
        this.isMobileView = false;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.loaderService.showSpinner();
    this.proposalId = this.route.snapshot.params['id'];
    this.getProposalSettings();

    this.proposalTemplateForm = this.fb.group({
      groupTemplateName: ['', [Validators.required]],
      paAgreement: ['SINGLE']
    });

    this.featureForms = new UntypedFormGroup({});
    this.newFeatureForm = new UntypedFormGroup({
      name: new UntypedFormControl(),
      description: new UntypedFormControl(),
      installHours: new UntypedFormControl(),
      removalHours: new UntypedFormControl(),
      costHidden: new UntypedFormControl(false),
      isOptional: new UntypedFormControl(false)
    });

    this.featureItemForm = new UntypedFormGroup({
      inventoryItemCategory: new UntypedFormControl(),
      inventoryItem: new UntypedFormControl(),
      name: new UntypedFormControl(),
      quantity: new UntypedFormControl(),
      type: new UntypedFormControl('NEW'),
      cost: new UntypedFormControl(),
      price: new UntypedFormControl(),
      multiPrice: new UntypedFormControl(),
      hidden: new UntypedFormControl(false),
      quantityHidden: new UntypedFormControl(true),
      imageType: new UntypedFormControl('NONE'),
      isTaxable: new UntypedFormControl(),
      isArchived: new UntypedFormControl(false)
    });
    this.featureItemPropertyUpdated$.pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((request) => {
        this.updateFeatureItemBulk(request);
      });

    this.prepareFeatureNameTypeahead();
    await this.getFeaturesAndFeatureItems();
    this.getItemCategories();
    if (this.isAccepted) {
      this.featureForms.disable();
      this.featureItemForm.disable();
      this.newFeatureForm.disable();
    } else {
      this.featureForms.enable();
      this.featureItemForm.enable();
      this.newFeatureForm.enable();
    }

    this.proposalTemplateForm.controls.groupTemplateName.valueChanges.pipe(
      debounceTime(1500),
      distinctUntilChanged(),
      filter(value => {
        return value && value.trim() !== '';
      })
    )
      .subscribe((value) => {
        if (value) {
          let data = {
            groupTemplateName: value.trim(),
            groupTemplateId: this.proposalTemplate.groupTemplateId
          }
          this.featureGroupTemplateService.updateProposalTemplateName(data).subscribe(() => {
            this.alertService.showSuccessAlert('Successfully updated template');
            this.getFeaturesAndFeatureItems();
          })
        }
      });
    this.checkScreenSize();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.features, event.previousIndex, event.currentIndex);
    this.updateFeatureOrder(this.features, true)
  }

  getIndex(id: string) {
    return this.rawFeatures.findIndex(feat => feat.featureTemplateId === id)
  }

  ngOnDestroy(): void {
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }
    if (this.featureItemPropertyUpdated$) {
      this.featureItemPropertyUpdated$.unsubscribe();
    }
  }

  // Open Feature Modal
  openFeatureModal(): void {
    this.modalReference = this.modalService.open(this.featureModal);
    this.modalReference.dismissed.subscribe(() => {
      this.resetFeatureForm();
    });
  }

  // Open Feature Item Modal
  async openFeatureItemModal(featureId: string, shouldReset: boolean = true) {
    if(!this.selectedFeatureItem?.itemIsArchived) {
      this.showArchivedItems = false;
    }
    this.selectedFeatureId = featureId;
    if (shouldReset) await this.resetFeatureItemForm(shouldReset);
    this.modalReference = this.modalService.open(this.featureItemModal);
    this.modalReference.dismissed.subscribe(() => {
      this.resetFeatureItemForm();
    });
  }

  // Open Delete Feature Confirmation Modal
  openDeleteFeatureConfirmationModal(featureId: string): void {
    const confirmationModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmationModalRef.componentInstance.message = "Are you sure you would like to delete this feature?";
    confirmationModalRef.componentInstance.actionBtnTitle = "Delete";
    confirmationModalRef.componentInstance.confirmed.subscribe(() => {
      this.deleteFeature(featureId);
    });
  }

  // Open Delete Feature Item Confirmation Modal
  openDeleteFeatureItemConfirmationModal(featureItem?: FeatureItem): void {
    if (featureItem) this.selectedFeatureItem = featureItem;
    const confirmationModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmationModalRef.componentInstance.message = "Are you sure you would like to delete this feature item?";
    confirmationModalRef.componentInstance.actionBtnTitle = "Delete";
    confirmationModalRef.componentInstance.confirmed.subscribe(() => {
      this.deleteFeatureItem();
    });
  }

  /* ----- Data ----- */

  // Get Features And Feature Items
  public async getFeaturesAndFeatureItems(): Promise<void> {
    const proposalTemplate = ((await lastValueFrom(this.featureGroupTemplateService.getProposalTemplateById(this.proposalId))) as any);
    const tempFeature = proposalTemplate.features;
    this.proposalTemplate = { ...proposalTemplate };
    this.features = tempFeature;
    this.rawFeatures = tempFeature;
    // Clear price update selections when features are reloaded
    this.selectedFeatureItemsForPriceUpdate.clear();

    this.proposalTemplateForm.controls['groupTemplateName'].setValue(proposalTemplate?.groupTemplateName);
    this.proposalTemplateForm.updateValueAndValidity();
    await this.createFeatureFormGroups();
    //const featureItems = ((await lastValueFrom(this.featureItemService.getFeatureItems({ searchTerm: null, sortBy: 'fi.feature_item_name', sortDirection: 'ASC', limit: null, offset: null }))) as any).featureItems;
    const featureItems = [];
    this.featureItems.clear();
    for (const featureItem of featureItems) {
      if (this.featureItems.has(featureItem.featureId)) {
        const tempArray = this.featureItems.get(featureItem.featureId);
        tempArray.push(featureItem);
        this.featureItems.set(featureItem.featureId, tempArray);
      } else {
        this.featureItems.set(featureItem.featureId, [featureItem]);
      }
    }
    this.loaderService.hideSpinner(500);
  }

  // Load Multi Options
  private loadMultiOptions(): void {
    // if (!this.proposal.multiOptions) return;
    // this.multiOptionKeys = Object.keys(this.proposal.multiOptions);
    // this.selectedMultiOptionKey = this.multiOptionKeys[0];
  }

  // Get Feature Items For Feature
  getFeatureItems(featureId: string): FeatureItem[] {
    if (!this.featureItems.has(featureId)) return [];
    return this.featureItems.get(featureId);
  }

  // Get Feature Item Count
  getFeatureItemCount(featureId: string): number {
    if (!this.featureItems.has(featureId)) return 0;
    return this.featureItems.get(featureId).length;
  }

  /* ----- Features ----- */

  // Add Feature
  addFeature(): void {
    if (this.newFeatureForm.valid) {
      const feature = {
        groupTemplateId: this.proposalTemplate?.groupTemplateId,
        featureName: this.newFeatureForm.value.name,
        featureDescription: this.newFeatureForm.value.description,
        featureInstallHours: this.newFeatureForm.value.installHours,
        featureRemovalHours: this.newFeatureForm.value.removalHours,
        featureCostHidden: this.newFeatureForm.value.costHidden,
        featureIsOptional: this.newFeatureForm.value.isOptional || false,
        featureIncluded: this.newFeatureForm.value.isSelected || true,
        featureIsHidden: false,
        featureIsCollapse: false,
        featureOrder: this.features.length + 1
      };
      this.featureGroupTemplateService.addFeatureinGroupTemplate(feature).subscribe(() => {
        this.alertService.showSuccessAlert('Feature Added');
        this.modalReference.close();
        this.getFeaturesAndFeatureItems();
        this.resetFeatureForm();

      });
    } else {
      this.newFeatureForm.markAllAsTouched();
    }
  };

  // Update Feature Order
  updateFeatureOrder(features: any, refresh: boolean = false): void {
    if (features.length != 0 && refresh) {
      let order = this.extractIds(features);
      this.featureGroupTemplateService.updateFeatureOrder(order).subscribe(() => {
        this.alertService.showSuccessAlert('Feature Order Updated');
        if (refresh) this.saved.emit();
      });
    }
  }

  extractIds<T extends { featureTemplateId: string }>(arr: T[]) {
    return arr.map(({ featureTemplateId }) => ({ featureTemplateId }));
  }

  // Delete Feature
  private deleteFeature(featureId: string): void {
    this.featureGroupTemplateService.deleteFeature(featureId).subscribe(() => {
      this.alertService.showSuccessAlert('Feature Deleted');
      this.saved.emit();
      this.getFeaturesAndFeatureItems();
    });
  }

  // Bulk Update Feature Property
  bulkUpdateFeatureProperty(property: string, value: boolean): void {
    this.featureGroupTemplateService.bulkUpdateFeatureProperty(this.proposalTemplate?.groupTemplateId, property, value).subscribe(() => {
      this.alertService.showSuccessAlert('Features Updated');
      this.saved.emit();
      this.getFeaturesAndFeatureItems();
    });
  }

  // Prepare Feature Name Typeahead
  private prepareFeatureNameTypeahead(): void {
    this.newFeatureNameTypeaheadFocus$ = new Subject<string>();
    this.newFeatureNameTypeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(debounceTime(250), distinctUntilChanged());
      return merge(debouncedText$, this.newFeatureNameTypeaheadFocus$).pipe(switchMap((term) => {
        return this.getFeatureNames((term.length == 0) ? null : term);
      }));
    }
  }

  // Get Feature Name
  private getFeatureNames(term: string): Observable<string[]> {
    const params = {
      searchTerm: term,
      sortBy: 'feature_name',
      sortDirection: 'ASC',
      limit: 10,
      offset: 0,
      column: 'feature_name'
    };
    return this.featureGroupTemplateService.getDistinctColumnValues(params);
  }

  // Reset Feature Form
  private resetFeatureForm(): void {
    this.newFeatureForm.reset();
    this.newFeatureForm.controls.name.setValue(null);
    this.newFeatureForm.controls.description.setValue(null);
    this.newFeatureForm.controls.installHours.setValue(null);
    this.newFeatureForm.controls.removalHours.setValue(null);
    this.newFeatureForm.controls.costHidden.setValue(false);
  }

  // New Feature Form Accessors
  get newFeatureName() { return this.newFeatureForm.controls.name; }
  get newFeatureInstallHours() { return this.newFeatureForm.controls.installHours; }
  get newFeatureRemovalHours() { return this.newFeatureForm.controls.removalHours; }

  // Create Feature Form Groups
  private async createFeatureFormGroups(): Promise<void> {
    this.featureNameTypeaheads.clear();
    for (const formGroup in this.featureForms.controls) this.featureForms.removeControl(formGroup);
    for (const feature of this.features) {
      const formGroup = this.fb.group({
        name: [feature.name],
        description: [feature.description],
        installHours: [feature.installHours],
        removalHours: [feature.removalHours],
        costHidden: [feature.costHidden],
        // included: [!feature.included],
        isOptional: [feature.isOptional],
        isSelected: [feature.isSelected],
        isHidden: [feature.isHidden]
      });

      // formGroup.patchValue({ name: feature.name }, { emitEvent: false })
      // formGroup.patchValue({ description: feature.description }, { emitEvent: false })
      // formGroup.patchValue({ installHours: feature.installHours }, { emitEvent: false })
      // formGroup.patchValue({ removalHours: feature.removalHours }, { emitEvent: false })
      // formGroup.patchValue({ costHidden: feature.costHidden }, { emitEvent: false })
      // formGroup.patchValue({ included: !feature.included }, { emitEvent: false })
      // formGroup.patchValue({ isOptional: feature.isOptional }, { emitEvent: false })
      // formGroup.patchValue({ isSelected: feature.isSelected }, { emitEvent: false })

      formGroup.controls.costHidden.valueChanges.pipe(debounceTime(250), distinctUntilChanged())
        .subscribe((value) => {
          if (value != feature.costHidden) this.updateFeaturesArrayById('costHidden', feature.featureTemplateId, value);
        });

      formGroup.controls.isHidden.valueChanges.pipe(debounceTime(250), distinctUntilChanged())
        .subscribe((value) => {
          if (value != feature.isHidden) this.updateFeaturesArrayById('isHidden', feature.featureTemplateId, value);
        });

      formGroup.controls.isOptional.valueChanges.pipe(debounceTime(250), distinctUntilChanged())
        .subscribe((value) => {
          if (value != feature.isOptional) this.updateFeaturesArrayById('isOptional', feature.featureTemplateId, value);
        });

      formGroup.controls.isSelected.valueChanges.pipe(debounceTime(250), distinctUntilChanged())
        .subscribe((value) => {
          if (value != feature.isSelected) this.updateFeaturesArrayById('isSelected', feature.featureTemplateId, value);
        });

      this.featureForms.registerControl(feature.featureTemplateId, formGroup);
      this.featureNameTypeaheads.set(feature.featureTemplateId, this.createFeatureNameTypeahead());

    }
  }

  // Create Feature Name Typeahead
  private createFeatureNameTypeahead(): { input: (text$: Observable<string>) => Observable<string[]>, focus$: Subject<string> } {
    const typeaheadFocus$ = new Subject<string>();
    const typeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(debounceTime(250), distinctUntilChanged());
      return merge(debouncedText$, typeaheadFocus$).pipe(switchMap((term) => {
        return this.getFeatureNames((term.length == 0) ? null : term);
      }));
    };
    return { input: typeaheadInput, focus$: typeaheadFocus$ };
  }

  // Get Feature Name Typeahead Input
  getFeatureNameTypeaheadInput(featureId: string): (text$: Observable<string>) => Observable<string[]> {
    return this.featureNameTypeaheads.get(featureId).input;
  }

  // Feature Name Typeahead Focused
  featureNameTypeaheadFocused(featureId: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.featureNameTypeaheads.get(featureId).focus$.next(value);
  }

  // Is Nested Feature Form Control Invalid
  isNestedFeatureFormControlInvalid(featureId: string, controlName: string): boolean {
    const formControl = (this.featureForms.controls[featureId] as UntypedFormGroup).controls[controlName];
    return formControl.invalid && (formControl.dirty || formControl.touched);
  }

  // Get Nested Feature Form Control Errors
  getNestedFeatureFormControlErrors(featureId: string, controlName: string): ValidationErrors {
    return (this.featureForms.controls[featureId] as UntypedFormGroup).controls[controlName].errors;
  }

  /* ----- Feature Items ----- */

  // Save Feature Item
  saveFeatureItem(): void {
    this.selectedFeatureItem ? this.updateFeatureItem() : this.addFeatureItem();
  }

  // Add Feature Item
  private addFeatureItem(): void {
    try {
      this.validateMultiPricing(this.selectedMultiOptionPricing);
      if (this.featureItemForm.valid) {
        const featureItem = {
          featureTemplateId: this.selectedFeatureId,
          itemId: this.featureItemForm.value.inventoryItem,
          itemCategory: this.featureItemForm.value.inventoryItemCategory,
          name: this.featureItemForm.value.name,
          quantity: this.featureItemForm.value.quantity,
          type: this.featureItemForm.value.type,
          cost: this.featureItemForm.value.cost,
          price: this.featureItemForm.value.price,
          multiPricing: this.selectedMultiOptionPricing,
          hidden: this.featureItemForm.value.hidden || false,
          quantityHidden: this.featureItemForm.value.quantityHidden,
          imageType: this.featureItemForm.value.imageType ? this.featureItemForm.value.imageType : 'NONE',
          isTaxable: this.featureItemForm.value.isTaxable || this.featureItemForm.value.isTaxable == null ? true : false
        };
        this.featureGroupTemplateService.addFeatureItemInGroupTemplate(featureItem).subscribe(() => {
          this.alertService.showSuccessAlert('Feature Item Added');
          this.modalReference.close();
          this.saved.emit();
          this.getFeaturesAndFeatureItems();
          this.resetFeatureItemForm();
          // NULL Feature ID variable?
        });
      } else {
        this.featureItemForm.markAllAsTouched();
      }
    } catch (error) {
      this.alertService.showWarningAlert(error.message);
    }
  };

  // Update Feature Item
  private updateFeatureItem(): void {
    try {
      this.validateMultiPricing(this.selectedMultiOptionPricing);
      if (this.featureItemForm.valid) {
        const featureItem = {
          featureItemTemplateId: this.selectedFeatureItem.id,
          itemId: this.featureItemForm.value.inventoryItem,
          quantity: this.featureItemForm.value.quantity,
          type: this.featureItemForm.value.type,
          cost: this.featureItemForm.value.cost,
          price: this.featureItemForm.value.price,
          multiPricing: this.selectedMultiOptionPricing,
          hidden: this.featureItemForm.value.hidden || false,
          quantityHidden: this.featureItemForm.value.quantityHidden,
          imageType: this.featureItemForm.value.imageType,
          isTaxable: this.featureItemForm.value.isTaxable || this.featureItemForm.value.isTaxable == null ? true : false,
          featureTemplateId: this.selectedFeatureId,
          itemCategory: this.featureItemForm.value.inventoryItemCategory,
          name: this.featureItemForm.value.name,
        };
        this.featureGroupTemplateService.updateFeatureItemInGroupTemplate(featureItem).subscribe(() => {
          this.alertService.showSuccessAlert('Feature Item Updated');
          this.modalReference.close();
          this.saved.emit();
          this.getFeaturesAndFeatureItems();
          this.resetFeatureItemForm();
        });
      } else {
        this.featureItemForm.markAllAsTouched();
      }
    } catch (error) {
      this.alertService.showWarningAlert(error.message);
    }
  }

  // Delete Feature Item
  private deleteFeatureItem(): void {
    this.featureGroupTemplateService.deleteFeatureItem(this.selectedFeatureItem.id).subscribe(() => {
      this.alertService.showSuccessAlert('Feature Item Deleted');
      if (this.modalReference) this.modalReference.close();
      this.saved.emit();
      this.getFeaturesAndFeatureItems();
      this.resetFeatureItemForm();
    });
  }

  // Edit Feature Item
  async editFeatureItem(featureItem: FeatureItem): Promise<void> {
    this.selectedFeatureItem = featureItem;
    if(this.selectedFeatureItem?.itemIsArchived) {
      this.featureItemForm.controls.isArchived.patchValue(true);
      this.showArchivedItems = true;
    }
    this.featureItemForm.controls.inventoryItemCategory.setValue(featureItem.itemCategory);
    await this.itemCategorySelected(featureItem.itemCategory);
    this.featureItemForm.controls.quantity.setValue(featureItem.quantity);
    this.featureItemForm.controls.type.setValue(featureItem.type);
    this.featureItemForm.controls.cost.setValue(featureItem.cost);
    this.featureItemForm.controls.name.setValue(featureItem.itemName);
    this.featureItemForm.controls.price.setValue(featureItem.price);
    this.selectedMultiOptionPricing = featureItem.multiPricing;
    if (this.pagreementType == 'MULTI') this.selectMultiOption('A');
    this.featureItemForm.controls.hidden.setValue(featureItem.hidden ? featureItem.hidden : false);
    this.featureItemForm.controls.quantityHidden.setValue(featureItem.quantityHidden);
    this.featureItemForm.controls.imageType.setValue(featureItem.imageType);
    this.featureItemForm.controls.isTaxable.setValue(featureItem.isTaxable != null ? featureItem.isTaxable : true);
    this.openFeatureItemModal(featureItem.featureId, false);
  }

  collapseFeatures() {
    this.features.map(feature => this.updateCollapse(feature, true));
  }

  expandFeatures() {
    this.features.map(feature => this.updateCollapse(feature, false));
  }

  updateCollapse(feature: Feature, collapse: boolean) {
    // Implementation can be added here if needed
  }

  // Toggle Feature Item Options Popover
  toggleFeatureItemOptionsPopover(popover: NgbPopover, featureItem: FeatureItem, feature: any, featureItemIndex: number): void {
    if (popover.isOpen()) popover.close();
    else popover.open({ featureItem: featureItem, feature: feature, featureItemIndex: featureItemIndex });
  }

  // Adjust Feature Item Property
  adjustFeatureItemProperty(feature: any, featureItemIndex: any, property: any, value: any): void {
    // A cleared number input reports an empty value, which the API rejects
    if (value === '' || value == null) value = 0;
    this.featureItemPropertyUpdated$.next({ feature: feature, featureItemIndex: featureItemIndex, property: property, value: value });
  }

  //Bulk Update Feature Item Property
  bulkUpdateFeatureItemProperty(property: string, value: boolean, refresh: boolean = false): void {
    this.featureGroupTemplateService.bulkUpdateFeatureItemProperty(this.proposalTemplate?.groupTemplateId, property, value).subscribe(() => {
      this.alertService.showSuccessAlert('Features Updated');
      this.saved.emit();
      this.getFeaturesAndFeatureItems();
    });
    // this.featureItemService.bulkUpdateFeatureItemProperty(this.proposal.id, property, value).subscribe(() => {
    //   this.alertService.showSuccessAlert('Feature Items Updated');
    //   this.saved.emit();
    //   this.getFeaturesAndFeatureItems();

    // });
  }

  // Multi Option Is Enabled
  multiOptionIsEnabled(key: string): boolean {
    return this.multiOptionsRef[key].isEnabled;
  }

  // Select Multi Option
  selectMultiOption(key: string): void {
    this.selectedMultiOptionKey = key;
    if (this.selectedMultiOptionPricing && this.selectedMultiOptionPricing[key]) {
      this.featureItemForm.controls.multiPrice.setValue(this.selectedMultiOptionPricing[key]);
    } else {
      this.featureItemForm.controls.multiPrice.setValue(null);
    }
  }

  // Multi Option Pricing Changed
  multiOptionPricingChanged(price: number): void {
    if (!this.selectedMultiOptionPricing) this.selectedMultiOptionPricing = {};
    this.selectedMultiOptionPricing[this.selectedMultiOptionKey] = price;
  }

  // Adjust Multi Option Price
  adjustMultiOptionPrice(featureItem: FeatureItem, key: string, value: any, featureItemIndex: number, feature: any): void {
    let multiOptionPricing = featureItem.multiPricing;
    multiOptionPricing[key] = value === '' || value == null ? 0 : value;

    this.featureItemPropertyUpdated$.next({ feature: feature, featureItemIndex: featureItemIndex, property: 'multiPricing', value: multiOptionPricing });
  }

  // Validate Multi Pricing
  private validateMultiPricing(multiPricing: any): void {
    const max = Infinity;
    const min = 0;
    for (const option in multiPricing) {
      const field = `Price (Multi) value`;
      const value = multiPricing[option];
      if (value > max) {
        this.selectMultiOption(option);
        throw new Error(`${field} cannot be greater than ${max}.`);
      }
      if (value < min) {
        this.selectMultiOption(option);
        throw new Error(`${field} cannot be less than ${min}.`);
      }
    }
  }

  // Reset Feature Item Form
  private async resetFeatureItemForm(shouldReset?: boolean): Promise<void> {
    this.selectedFeatureItem = null;
    this.featureItemForm.reset();
    this.featureItemForm.controls.quantity.setValue(null);
    this.featureItemForm.controls.type.setValue('NEW');
    this.featureItemForm.controls.inventoryItemCategory.setValue(this.itemCategories[0]);
    await this.itemCategorySelected(this.itemCategories[0]);
    this.featureItemForm.controls.hidden.setValue(false);
    this.featureItemForm.controls.quantityHidden.setValue(true);
    this.featureItemForm.controls.imageType.setValue('NONE');
    this.featureItemForm.controls.isTaxable.setValue(true);
  }

  // Feature Item Form Accessors
  get featureItemQuantity() { return this.featureItemForm.controls.quantity; }
  get featureItemCost() { return this.featureItemForm.controls.cost; }
  get featureItemPrice() { return this.featureItemForm.controls.price; }
  get featureItemMultiPrice() { return this.featureItemForm.controls.multiPrice; }

  /* ----- Inventory ----- */

  // Get Item Categories
  private getItemCategories(): void {
    const params = {
      searchTerm: null,
      sortBy: 'i.item_category',
      sortDirection: 'ASC',
      limit: null,
      offset: null,
      column: 'i.item_category'
    };
    this.inventoryService.getDistinctColumnValues(params).subscribe((values) => {
      this.itemCategories = values;
    });
  }

  // Item Category Selected
  async itemCategorySelected(category: string, isAutoSelected: boolean = true): Promise<void> {
    if (!this.itemCategoryItems.has(category)) {
      const params = {
        searchTerm: null,
        sortBy: 'item_name',
        sortDirection: 'ASC',
        limit: null,
        offset: null,
        'filter:i.item_category': JSON.stringify([category])
      };
      const res = await lastValueFrom(this.inventoryService.getItems(params));
      this.selectedItemCategoryItems = res.items;
      this.itemCategoryItems.set(category, this.selectedItemCategoryItems);
    } else {
      this.selectedItemCategoryItems = this.itemCategoryItems.get(category);
    }
    this.selectedItemCategoryItems = this.selectedItemCategoryItems;
    this.currentCategoryItems = this.selectedItemCategoryItems;
    this.filterCategoryItems(this.showArchivedItems, isAutoSelected);

  }

  toggleShowArchivedItems(): void {
    this.showArchivedItems = !this.showArchivedItems;
    this.filterCategoryItems(this.showArchivedItems, true);
  }

  filterCategoryItems(showArchivedItems: boolean, isAutoSelected: boolean): void {
    let items = this.currentCategoryItems;
    if (showArchivedItems) {
      items = this.currentCategoryItems;
    } else {
      items = this.currentCategoryItems.filter((item: any) => !item.isArchived);
    }
    this.selectedItemCategoryItems = items;
    const itemId = (isAutoSelected && this.selectedFeatureItem) ? this.selectedFeatureItem.itemId : this.selectedItemCategoryItems[0]?.id;
    this.featureItemForm.controls.inventoryItem.setValue(itemId);
    if(this.selectedItemCategoryItems?.length <= 0) {
      this.featureItemForm.controls.inventoryItem?.disable();
    } else {
      this.featureItemForm.controls.inventoryItem?.enable();
    }
    this.itemSelected(itemId, isAutoSelected);
  }

  // Item Selected
  itemSelected(itemId: string, isAutoSelected: boolean): void {
    const item = this.selectedItemCategoryItems.find((item) => { return item.id == itemId; });
    if (item) {
      this.selectedItemStockLevels = { stockCurrentLevel: item.stockCurrentLevel, stockAdjustedLevel: item.stockAdjustedLevel };
      if (!this.selectedFeatureItem || (this.selectedFeatureItem && ((this.selectedFeatureItem.itemId != item.id) || !isAutoSelected))) {
        this.featureItemForm.controls.name.setValue(item.name);
        this.featureItemForm.controls.cost.setValue(item.cost);
        this.featureItemForm.controls.price.setValue(item.price);
        this.selectedMultiOptionPricing = item.multiPricing;
      }
    }
  }

  updateFeaturesArray(property: any, index: number, value: any) {
    this.features[index][property] = value;
    let transformedData = this.transformFeatureObjects(this.features[index])

    let data: any = {
      ...transformedData,
    }

    this.featureGroupTemplateService.updateTemplateProposal(data).subscribe(() => {
      if (property !== 'isCollapse') {
        this.getFeaturesAndFeatureItems();
      }
    })
  }

  updateFeaturesArrayById(property: any, id: any, value: any) {
    let feature = this.features.find(x => x.featureTemplateId === id)
    feature[property] = value;
    let transformedData = this.transformFeatureObjects(feature)

    let data: any = {
      ...transformedData,
    }

    this.featureGroupTemplateService.updateTemplateProposal(data).subscribe(() => {
      this.alertService.showSuccessAlert('Successfully updated template');
      this.getFeaturesAndFeatureItems();
    })
  }

  transformFeatureObjects(feature: any) {
    return {
      featureName: feature.name,
      featureDescription: feature.description,
      featureInstallHours: feature.installHours,
      featureRemovalHours: feature.removalHours,
      featureCostHidden: feature.costHidden,
      featureIncluded: feature.isSelected,
      featureIsOptional: feature.isOptional,
      featureOrder: feature.order,
      featureIsHidden: feature.isHidden,
      featureIsCollapse: feature.isCollapse,
      featureTemplateUpdatedBy: feature.updatedAt,
      featureTemplateUpdatedAt: feature.createdBy,
      featureTemplateId: feature.featureTemplateId,
    }
  }

  getTotalPricing(index): number {
    let ret = 0;
    if (this.features.length > 0) {
      this.features[index]?.featureItems.forEach(element => {
        ret = (element.cost * element.quantity) + ret;
      });
    }

    return ret;
  }

  updatePAtype(value: any) {
    this.pagreementType = value;
  }

  getProposalSettings() {
    this.settingService.getProposalSettings().subscribe((proposalSettings) => {
      if (proposalSettings?.agreementTypes?.multi) {
        this.multiOptionsRef = proposalSettings?.agreementTypes?.multi;
        this.multiOptionKeys = Object.keys(proposalSettings?.agreementTypes?.multi);
        this.selectedMultiOptionKey = this.multiOptionKeys[0];
      }
    })
  }

  updateFeatureItemBulk(data: any) {
    let feature = data?.feature
    let featureItem = feature?.featureItems[data?.featureItemIndex];
    let newFeatureItem = {
      ...featureItem
    }
    newFeatureItem[data?.property] = data.value;

    const payload = {
      featureItemTemplateId: newFeatureItem.id,
      itemId: newFeatureItem.itemId,
      quantity: newFeatureItem.quantity,
      type: newFeatureItem.type,
      cost: newFeatureItem.cost,
      price: newFeatureItem.price,
      multiPricing: newFeatureItem.multiPricing,
      hidden: newFeatureItem.hidden || false,
      quantityHidden: newFeatureItem.quantityHidden,
      imageType: newFeatureItem.imageType,
      isTaxable: newFeatureItem.isTaxable || newFeatureItem.isTaxable == null ? true : false,
      featureTemplateId: feature.featureTemplateId,
      itemCategory: newFeatureItem.itemCategory,
      name: newFeatureItem.itemName,
    };
    this.featureGroupTemplateService.updateFeatureItemInGroupTemplate(payload).subscribe(() => {
      this.alertService.showSuccessAlert('Feature Item Updated');
      this.getFeaturesAndFeatureItems();
    });
  }

  //Set isMobile value and set table limit base on screen size
  checkScreenSize(width?: number) {
    let screenWidth = width || window.innerWidth;
    this.isMobileView = screenWidth <= 768;
  }

  // Avoid collapsing if user is typing inside an input or textarea
  handleKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.key === ' ' || event.code === 'Space') {
      const input = event.target as HTMLInputElement;
      const cursorPos = input.selectionStart;
      const value = input.value;

      // insert space
      input.value = value.slice(0, cursorPos) + ' ' + value.slice(cursorPos);

      setTimeout(() => {
        input.selectionStart = input.selectionEnd = cursorPos + 1;
      });

      event.preventDefault();
      event.stopPropagation();

      const formControl = this.featureForms.get('name');
      if (formControl) {
        formControl.setValue(input.value);
      }
    }
  }

  /* ----- Price Update ----- */

  // Is Feature Item Selected For Price Update
  isFeatureItemSelectedForPriceUpdate(featureItem: any): boolean {
    const itemId = featureItem.id || featureItem.featureItemTemplateId || `${featureItem.featureName}-${featureItem.itemName}`;
    return this.selectedFeatureItemsForPriceUpdate.has(itemId);
  }

  // Toggle Feature Item For Price Update
  toggleFeatureItemForPriceUpdate(featureItem: any, checked: boolean): void {
    const itemId = featureItem.id || featureItem.featureItemTemplateId || `${featureItem.featureName}-${featureItem.itemName}`;
    if (checked) {
      this.selectedFeatureItemsForPriceUpdate.add(itemId);
    } else {
      this.selectedFeatureItemsForPriceUpdate.delete(itemId);
    }
  }

  // Are All Feature Items Selected For Price Update
  areAllFeatureItemsSelectedForPriceUpdate(feature: any): boolean {
    if (!feature.featureItems || feature.featureItems.length === 0) return false;
    return feature.featureItems.every((item: any) => this.selectedFeatureItemsForPriceUpdate.has(item.id));
  }

  // Toggle All Feature Items For Price Update
  toggleAllFeatureItemsForPriceUpdate(feature: any, checked: boolean): void {
    if (!feature.featureItems) return;
    feature.featureItems.forEach((item: any) => {
      if (checked) {
        this.selectedFeatureItemsForPriceUpdate.add(item.id);
      } else {
        this.selectedFeatureItemsForPriceUpdate.delete(item.id);
      }
    });
  }

  async updateSelectedPricesFromInventory(): Promise<void> {
    this.featureGroupTemplateService.getListOfItemWithUnmatchedPrices(this.proposalTemplate.groupTemplateId).subscribe((response) => {
      this.itemsToUpdate = response;
      // Clear previous selections when opening modal
      this.selectedFeatureItemsForPriceUpdate.clear();
      // Initialize all items as selected by default
      if (this.itemsToUpdate && this.itemsToUpdate.length > 0) {
        this.itemsToUpdate.forEach((item: any) => {
          const itemId = item.id || item.featureItemTemplateId || `${item.featureName}-${item.itemName}`;
          this.selectedFeatureItemsForPriceUpdate.add(itemId);
        });
      }
      this.modalReference = this.modalService.open(this.priceUpdateModal, { size: 'lg' });
    })

  }

  // Confirm Price Update From Inventory
  confirmPriceUpdateFromInventory(): void {
    if (!this.proposalTemplate?.groupTemplateId) {
      this.alertService.showWarningAlert('Group Template ID not found');
      return;
    }

    this.loaderService.showSpinner();

    const featureItemIds = Array.from(this.selectedFeatureItemsForPriceUpdate);

    this.featureGroupTemplateService.syncPricesFromInventory(this.proposalTemplate.groupTemplateId, featureItemIds).subscribe(() => {
        this.loaderService.hideSpinner(500);
        this.alertService.showSuccessAlert(`Successfully updated ${featureItemIds.length} item(s)`);
        this.selectedFeatureItemsForPriceUpdate.clear();
        this.itemsToUpdate = [];
        this.modalReference.close();
        this.getFeaturesAndFeatureItems();
    });
  }

}
